import React, { useState } from 'react';
import { Button, Card, CardBody, Modal, Input } from '../common';
import {
  useDetails,
  useAddDetail,
  useConfirmDetail,
  useSupportDetail,
  useToggleRejection,
} from '../../hooks/useDetails';
import { useReadyStatus, useToggleReady } from '../../hooks/useRounds';

/**
 * Detail Card Component
 */
const DetailCard = ({
  detail,
  isTop,
  isTargeted,
  canSupport,
  onSupport,
  supportLoading,
  currentUserId,
}) => {
  const isAuthor = detail.authorUserId?.id === currentUserId || detail.authorUserId?._id === currentUserId;
  const authorName = detail.authorUserId?.displayName || 'Unknown';

  return (
    <div className={`
      border rounded-lg p-3 transition-colors
      ${isTop ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white'}
      ${detail.status === 'rejected' ? 'opacity-50 line-through' : ''}
      ${detail.status === 'pending' ? 'border-dashed bg-gray-50' : ''}
    `}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isTop && detail.status === 'active' && (
              <span className="text-xs font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                TOP BID
              </span>
            )}
            {isTargeted && (
              <span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                TARGETED
              </span>
            )}
            {detail.status === 'pending' && (
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                Pending...
              </span>
            )}
            {detail.status === 'rejected' && (
              <span className="text-xs font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                REJECTED
              </span>
            )}
          </div>
          <p className="text-sm text-gray-800">"{detail.content}"</p>
          <p className="text-xs text-gray-500 mt-1">
            - {authorName}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg font-bold text-primary-600">
            {detail.totalBid || 1}
          </div>
          <div className="text-xs text-gray-500">bid</div>
        </div>
      </div>

      {canSupport && detail.status === 'active' && !isAuthor && (
        <div className="mt-2 pt-2 border-t">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onSupport(detail.id)}
            loading={supportLoading}
          >
            +1 Support
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Add Detail Modal
 */
const AddDetailModal = ({
  isOpen,
  onClose,
  onAdd,
  loading,
  balance,
  influenceCost = 1,
}) => {
  const [content, setContent] = useState('');
  const canAfford = balance >= influenceCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || !canAfford) return;

    try {
      await onAdd(content.trim());
      setContent('');
      onClose();
    } catch (err) {
      // Error handled by parent
    }
  };

  const handleClose = () => {
    setContent('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Detail" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Detail Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, 100))}
            placeholder='e.g., "A mysterious stranger enters..."'
            rows={2}
            maxLength={100}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            required
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>A "counter-prompt" the GM must incorporate</span>
            <span>{content.length}/100</span>
          </div>
        </div>

        <p className={`text-sm ${canAfford ? 'text-gray-600' : 'text-red-600'}`}>
          {canAfford
            ? `This will cost ${influenceCost} influence (you have ${balance}).`
            : `Not enough influence. You need ${influenceCost} but have ${balance}.`
          }
        </p>

        <div className="flex gap-3 justify-end pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={!canAfford}
          >
            Add Detail
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * GM Rejection Toggle
 */
const GMRejectionToggle = ({
  isRejecting,
  topDetailContent,
  rejectionCost,
  canAfford,
  onToggle,
  loading,
}) => {
  return (
    <div className={`
      border rounded-lg p-4 transition-colors
      ${isRejecting ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}
    `}>
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">Reject Top Detail</h4>
          {topDetailContent ? (
            <p className="text-sm text-gray-500 mt-1">
              Target: "{topDetailContent.substring(0, 40)}..."
            </p>
          ) : (
            <p className="text-sm text-gray-400 mt-1">No details to reject</p>
          )}
          {rejectionCost && (
            <p className={`text-sm mt-1 ${canAfford ? 'text-gray-600' : 'text-red-600'}`}>
              Cost: {rejectionCost} influence
              {!canAfford && ' (insufficient)'}
            </p>
          )}
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isRejecting}
            onChange={onToggle}
            disabled={loading || !topDetailContent || (!isRejecting && !canAfford)}
            className="sr-only peer"
          />
          <div className={`
            w-11 h-6 rounded-full peer transition-colors
            ${isRejecting ? 'bg-red-500' : 'bg-gray-300'}
            ${(!topDetailContent || (!isRejecting && !canAfford)) ? 'opacity-50 cursor-not-allowed' : ''}
            peer-focus:ring-2 peer-focus:ring-red-300
          `}>
            <div className={`
              absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
              ${isRejecting ? 'translate-x-5' : 'translate-x-0'}
            `} />
          </div>
        </label>
      </div>
    </div>
  );
};

/**
 * Ready Status Section - shows who is ready and allows player to toggle
 */
const ReadyStatusSection = ({
  gameId,
  roundId,
  isGM,
  currentUserId,
  userHasAddedDetails,
}) => {
  const { data: readyData, isLoading } = useReadyStatus(gameId, roundId, 'influence');
  const toggleReady = useToggleReady(gameId, roundId);

  if (isLoading) {
    return (
      <div className="animate-pulse flex items-center gap-2">
        <div className="h-4 w-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
    );
  }

  const players = readyData?.players || [];
  const readyCount = readyData?.readyCount || 0;
  const totalPlayers = readyData?.totalPlayers || 0;
  const currentUserReady = readyData?.currentUserReady || false;
  const allReady = readyData?.allReady || false;

  const handleToggle = async () => {
    try {
      await toggleReady.mutateAsync();
    } catch (err) {
      // Error handled by mutation
    }
  };

  // Dynamic hint message for players
  const getReadyHint = () => {
    if (userHasAddedDetails) {
      return 'Check if you do not plan to add any other details';
    }
    return 'Your balance will be set to at least 2';
  };

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700">Player Readiness</h4>
        <span className={`text-sm font-medium ${allReady ? 'text-green-600' : 'text-amber-600'}`}>
          {readyCount}/{totalPlayers} ready
        </span>
      </div>

      {/* Player list with ready status */}
      <div className="space-y-2 mb-3">
        {players.map((player) => (
          <div
            key={player.id}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
              player.isReady ? 'bg-green-50' : 'bg-gray-50'
            }`}
          >
            <span className="text-gray-700">{player.displayName}</span>
            {player.isReady ? (
              <span className="text-green-600 font-medium">✓ Ready</span>
            ) : (
              <span className="text-gray-400">Waiting...</span>
            )}
          </div>
        ))}
      </div>

      {/* Player: Ready checkbox */}
      {!isGM && (
        <div className="pt-3 border-t">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={currentUserReady}
              onChange={handleToggle}
              disabled={toggleReady.isPending}
              className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              I'm ready to proceed
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-8">
            {getReadyHint()}
          </p>
        </div>
      )}

      {/* GM: Status summary */}
      {isGM && (
        <div className={`mt-2 p-2 rounded text-sm ${allReady ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
          {allReady
            ? '✓ All players are ready to proceed'
            : `Waiting for ${totalPlayers - readyCount} player${totalPlayers - readyCount !== 1 ? 's' : ''} to ready up`
          }
        </div>
      )}
    </div>
  );
};

/**
 * Main Influence Phase View Component
 */
export const InfluencePhaseView = ({
  gameId,
  roundId,
  isGM,
  balance,
  currentUserId,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState(null);

  const { data: detailsData, isLoading } = useDetails(gameId, roundId);
  const addDetail = useAddDetail(gameId, roundId);
  const confirmDetail = useConfirmDetail(gameId, roundId);
  const supportDetail = useSupportDetail(gameId, roundId);
  const toggleRejection = useToggleRejection(gameId, roundId);

  const details = detailsData?.details || [];
  const topDetailId = detailsData?.topDetailId;
  const gmRejectionActive = detailsData?.gmRejectionActive || false;
  const rejectionCost = detailsData?.rejectionCost;

  // Find top detail for GM view
  const topDetail = details.find(d => d.id === topDetailId);

  // Check if current user has added any details
  const userHasAddedDetails = details.some(d => {
    const authorId = d.authorUserId?.id || d.authorUserId?._id;
    return authorId === currentUserId;
  });

  const handleAddDetail = async (content) => {
    setError(null);
    try {
      // Add detail (creates in pending state)
      const result = await addDetail.mutateAsync(content);
      // Immediately confirm it (makes it active)
      await confirmDetail.mutateAsync(result.detail.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add detail');
      throw err;
    }
  };

  const handleSupportDetail = async (detailId) => {
    setError(null);
    try {
      await supportDetail.mutateAsync(detailId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to support detail');
    }
  };

  const handleToggleRejection = async () => {
    setError(null);
    try {
      await toggleRejection.mutateAsync();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle rejection');
    }
  };

  // Calculate GM's ability to afford rejection
  const gmCanAfford = rejectionCost ? balance >= rejectionCost : false;

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <h3 className="font-semibold text-gray-900 mb-4">Influence the Prompt</h3>

        {/* GM Rejection Status Banner */}
        <div className={`
          rounded-lg p-3 text-sm mb-4
          ${gmRejectionActive
            ? 'bg-red-50 border border-red-200 text-red-700'
            : 'bg-green-50 border border-green-200 text-green-700'
          }
        `}>
          {gmRejectionActive
            ? '⚠️ GM is rejecting details this round'
            : '✓ GM is not rejecting details'
          }
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* GM Rejection Toggle */}
        {isGM && (
          <div className="mb-4">
            <GMRejectionToggle
              isRejecting={gmRejectionActive}
              topDetailContent={topDetail?.content}
              rejectionCost={rejectionCost}
              canAfford={gmCanAfford}
              onToggle={handleToggleRejection}
              loading={toggleRejection.isPending}
            />
          </div>
        )}

        {/* Details Section */}
        <div className="border-t pt-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">Details</h4>
            {!isGM && (
              <button
                onClick={() => setShowAddModal(true)}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                + Add
              </button>
            )}
          </div>

          {details.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">
              No details added yet. Players can add details to challenge the GM.
            </p>
          ) : (
            <div className="space-y-3">
              {details.map((detail, index) => (
                <DetailCard
                  key={detail.id}
                  detail={detail}
                  isTop={index === 0 && detail.status === 'active'}
                  isTargeted={gmRejectionActive && index === 0 && detail.status === 'active'}
                  canSupport={!isGM && balance >= 1}
                  onSupport={handleSupportDetail}
                  supportLoading={supportDetail.isPending}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </div>

        {/* Player Readiness Section */}
        <ReadyStatusSection
          gameId={gameId}
          roundId={roundId}
          isGM={isGM}
          currentUserId={currentUserId}
          userHasAddedDetails={userHasAddedDetails}
        />
      </CardBody>

      {/* Add Detail Modal */}
      <AddDetailModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddDetail}
        loading={addDetail.isPending || confirmDetail.isPending}
        balance={balance}
      />
    </Card>
  );
};
